import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { User, Session } from "@supabase/supabase-js";
import { checkUserRole } from "@/lib/userUtils";
import { Skeleton } from "@/components/ui/skeleton";

// Protected routes that require authentication
const PROTECTED_ROUTES = ["/dashboard", "/onboarding"];

// Dashboard routes accessible without company_id (user profile pages)
const COMPANY_OPTIONAL_ROUTES = [
  "/dashboard/settings",
  "/dashboard/help",
];

const isCompanyOptionalRoute = (pathname: string) =>
  COMPANY_OPTIONAL_ROUTES.some((route) => pathname.startsWith(route));

export const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const redirectingRef = useRef(false);

  const checkOnboardingStatus = useCallback(async (userId: string) => {
    try {
      const isGlobalAdmin = await checkUserRole("global_admin");
      if (isGlobalAdmin) return;

      const { data: profile } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("user_id", userId)
        .single();

      if (!profile?.company_id && location.pathname.startsWith("/dashboard")) {
        if (isCompanyOptionalRoute(location.pathname)) return;

        const skipRedirect = sessionStorage.getItem("skip_onboarding_redirect");
        if (skipRedirect) {
          sessionStorage.removeItem("skip_onboarding_redirect");
          return;
        }
        navigate("/onboarding", { replace: true });
      }
    } catch (error) {
      console.error("Error in checkOnboardingStatus:", error);
    }
  }, [navigate, location.pathname]);

  const redirectToAuth = useCallback(() => {
    if (redirectingRef.current) return;
    redirectingRef.current = true;
    
    // Store the current path to redirect back after login
    const currentPath = location.pathname + location.search;
    if (currentPath !== "/auth" && currentPath !== "/") {
      sessionStorage.setItem("redirect_after_auth", currentPath);
    }
    
    navigate("/auth", { replace: true });
  }, [navigate, location.pathname, location.search]);

  const isProtectedRoute = useCallback((pathname: string) => {
    return PROTECTED_ROUTES.some(route => pathname.startsWith(route));
  }, []);

  useEffect(() => {
    let isMounted = true;
    redirectingRef.current = false;

    // Initial auth check
    const initializeAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (!isMounted) return;

        // Check for session errors (token expired, invalid, etc.)
        if (error || !session?.user) {
          // If on a protected route, redirect to auth
          if (isProtectedRoute(location.pathname)) {
            redirectToAuth();
            return;
          }
          setSession(null);
          setUser(null);
          setLoading(false);
          return;
        }

        setSession(session);
        setUser(session.user);

        // Not authenticated and trying to access protected routes
        if (!session.user && isProtectedRoute(location.pathname)) {
          redirectToAuth();
          return;
        }

        // Authenticated user on landing page – route them appropriately
        if (session.user && location.pathname === "/") {
          const isGlobalAdmin = await checkUserRole("global_admin");
          if (isGlobalAdmin) {
            navigate("/dashboard", { replace: true });
            return;
          }

          // Optimized: Only fetch company_id, not full profile
          const { data: profile } = await supabase
            .from("profiles")
            .select("company_id")
            .eq("user_id", session.user.id)
            .maybeSingle(); // Use maybeSingle to avoid error if no profile

          if (profile?.company_id) {
            navigate("/dashboard", { replace: true });
          } else {
            navigate("/onboarding", { replace: true });
          }
          return;
        }

        // Authenticated user on dashboard – ensure onboarding status is valid
        if (session.user && location.pathname.startsWith("/dashboard")) {
          await checkOnboardingStatus(session.user.id);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        // On error, if on protected route, redirect to auth
        if (isProtectedRoute(location.pathname)) {
          redirectToAuth();
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Listen for auth state changes (including token expiration)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!isMounted) return;

        console.log("Auth state changed:", event, session?.user?.id);

        // Handle token refresh failures and sign out events
        if (event === "SIGNED_OUT" || event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
          // If token refresh failed or user signed out
          if (!session?.user) {
            setSession(null);
            setUser(null);
            
            // If on a protected route, redirect to auth
            if (isProtectedRoute(location.pathname)) {
              redirectToAuth();
            }
            return;
          }
        }

        // Handle sign in
        if (event === "SIGNED_IN" && session?.user) {
          setSession(session);
          setUser(session.user);
          
          // If on auth page, redirect to dashboard/onboarding
          if (location.pathname === "/auth") {
            const isGlobalAdmin = await checkUserRole("global_admin");
            if (isGlobalAdmin) {
              navigate("/dashboard", { replace: true });
              return;
            }

            const { data: profile } = await supabase
              .from("profiles")
              .select("company_id")
              .eq("user_id", session.user.id)
              .single();

            const redirectPath = sessionStorage.getItem("redirect_after_auth");
            if (redirectPath && redirectPath.startsWith("/dashboard")) {
              sessionStorage.removeItem("redirect_after_auth");
              navigate(redirectPath, { replace: true });
            } else if (profile?.company_id) {
              navigate("/dashboard", { replace: true });
            } else {
              navigate("/onboarding", { replace: true });
            }
          }
        }

        // Handle session updates
        if (session) {
          setSession(session);
          setUser(session.user);
        } else {
          // Session is null - token expired or invalid
          setSession(null);
          setUser(null);
          
          if (isProtectedRoute(location.pathname)) {
            redirectToAuth();
          }
        }
      }
    );

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, location.pathname, checkOnboardingStatus, isProtectedRoute, redirectToAuth]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-8">
        <div className="w-full max-w-4xl space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-8 w-64 mx-auto" />
            <Skeleton className="h-4 w-96 mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="space-y-3">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
